import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { useFormContext, useFormState } from "react-hook-form";

import { ControlledInput } from "../../Controllers";
import { mapsLoader } from "../../mapUtils";
import { ControlledStateSelect } from "../ControlledStateSelect";
import { useStates } from "../hooks/useStates";
import { getInputErrorMessage } from "../service";

import { optionsAddressComponents } from "./options";
import { AddressContainer, AddressStateZipContainer } from "./styles";
import { AutocompleteState, FullAddressAutocompleteProps } from "./types";

function FullAddressAutoComplete({
  control,
  addressName,
  cityName,
  stateName,
  zipcodeName,
  defaultValues = {},
  isDuplicateAddressError = false,
  setIsDuplicateAddressError,
}: FullAddressAutocompleteProps) {
  const [{ autocomplete, listener }, setAutocompleteState] = useState<AutocompleteState>({
    // @ts-expect-error TS2322
    autocomplete: null,
    // @ts-expect-error TS2322
    listener: null,
  });
  const { stateOptions, loading } = useStates();
  const defaultStateId = stateOptions.find((state) => state.id === defaultValues?.state)?.id;

  const { errors } = useFormState({ control, exact: true });
  const { setValue } = useFormContext();
  const duplicateAddressError = isDuplicateAddressError ? "error" : null;
  useEffect(() => {
    if (!loading) {
      mapsLoader
        .load()
        // eslint-disable-next-line promise/prefer-await-to-then
        .then(
          (google: {
            maps: {
              places: {
                Autocomplete: new (
                  arg0: HTMLInputElement,
                  arg1: { fields: string[]; strictBounds: boolean; types: string[] }
                ) => any;
              };
            };
          }) => {
            const inputElement = document.getElementById(
              "autocomplete-address"
            ) as HTMLInputElement;
            const ac = new google.maps.places.Autocomplete(inputElement, optionsAddressComponents);
            const acListener = ac.addListener("place_changed", async () => {
              const place = ac.getPlace();
              const addressComponents = place.address_components.reduce(
                (
                  acc: { [key: string]: string },
                  addressComponent: { long_name: string; types: string[] }
                ) => {
                  acc[addressComponent.types[0]] = addressComponent["long_name"];
                  return acc;
                },
                {}
              );
              // @ts-expect-error TS2532
              const stateId = stateOptions.find(
                (state) => state.label === addressComponents["administrative_area_level_1"]
              ).id;
              setValue(
                addressName,
                `${addressComponents["street_number"] || ""} ${addressComponents["route"]}`.trim(),
                { shouldValidate: true }
              );
              setValue(cityName, addressComponents["locality"], { shouldValidate: true });
              setValue(zipcodeName, addressComponents["postal_code"], { shouldValidate: true });
              setValue(stateName, stateId, { shouldValidate: true });
            });
            setAutocompleteState({ autocomplete: ac, listener: acListener });
          }
        )
        .catch((error: any) => {
          Sentry.captureException(error, {
            extra: { context: "Error loading Google Maps API: " },
          });
        });
      return () => {
        autocomplete?.unbindAll();
        listener?.remove();
      };
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  return (
    <AddressContainer>
      <ControlledInput
        control={control}
        label="Address"
        id="autocomplete-address"
        name={addressName}
        // @ts-expect-error TS2322
        error={
          getInputErrorMessage(errors[addressName]?.message as string) || duplicateAddressError
        }
        isHideErrorText={isDuplicateAddressError}
        defaultValue={defaultValues.address}
        onBlur={() => {
          if (isDuplicateAddressError) {
            // @ts-expect-error TS2722
            setIsDuplicateAddressError(false);
          }
        }}
      />
      <ControlledInput
        control={control}
        label="City"
        id="autocomplete-city"
        name={cityName}
        // @ts-expect-error TS2322
        error={getInputErrorMessage(errors[cityName]?.message as string) || duplicateAddressError}
        isHideErrorText={isDuplicateAddressError}
        defaultValue={defaultValues.city}
        onBlur={() => {
          if (isDuplicateAddressError) {
            // @ts-expect-error TS2722
            setIsDuplicateAddressError(false);
          }
        }}
      />
      <AddressStateZipContainer data-testid="stateAndZipCode">
        <ControlledStateSelect
          control={control}
          name={stateName}
          defaultValue={defaultStateId}
          // @ts-expect-error TS2322
          error={
            getInputErrorMessage(errors[stateName]?.message as string) || duplicateAddressError
          }
          isHideErrorText={isDuplicateAddressError}
        />
        <ControlledInput
          control={control}
          label="Zip code"
          id="autocomplete-zipcode"
          name={zipcodeName}
          // @ts-expect-error TS2322
          error={
            getInputErrorMessage(errors[zipcodeName]?.message as string) || duplicateAddressError
          }
          isHideErrorText={isDuplicateAddressError}
          defaultValue={defaultValues.zipcode}
          maxLength={5}
          onBlur={() => {
            if (isDuplicateAddressError) {
              // @ts-expect-error TS2722
              setIsDuplicateAddressError(false);
            }
          }}
        />
      </AddressStateZipContainer>
    </AddressContainer>
  );
}

// eslint-disable-next-line no-restricted-exports
export default FullAddressAutoComplete;
